import React, { useMemo, useState } from 'react';
import { getTimezoneMode, saveTimezoneMode } from '../../utils/storage';
import UsageDashboard from '../UsageDashboard';
import { CompactHeader } from '../ui/CompactHeader';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import {
  ManagementIcon,
  UsersIcon,
  RefreshIcon,
  DashboardIcon,
  CheckIcon
} from '../ui/Icons';
import { UI_CONSTANTS } from '../../constants/ui';
import { formatDate } from '../../utils/dateUtils';
import { ProjectSummaryItem } from './types';
import { useOrganizationData } from './hooks/useOrganizationData';
import { useUrlProjectSync } from './hooks/useUrlProjectSync';
import { usePaginatedUsers } from './hooks/usePaginatedUsers';
import { ProjectDetailView } from './components/ProjectDetailView';
import { ProjectSummaryTable } from './components/ProjectSummaryTable';
import { UserAnalysisTable } from './components/UserAnalysisTable';
import { UserFilterBar } from './components/UserFilterBar';
import { OrganizationSummaryCards } from './components/OrganizationSummaryCards';
import { EmptyState } from './components/EmptyState';
import { InfoNotice } from './components/InfoNotice';

export const ManagementDashboard: React.FC = () => {
  const [timezoneMode, setTimezoneMode] = useState(getTimezoneMode);
  const toggleTimezone = () => {
    const next = timezoneMode === 'utc' ? 'local' : 'utc';
    saveTimezoneMode(next);
    setTimezoneMode(next);
  };
  const { data, loading, error, fetchData } = useOrganizationData();
  const { viewMode, selectedProject, handleProjectClick, handleBackToOverview, handleViewUsage } = useUrlProjectSync(data);
  const {
    filter, setFilter,
    excludeFilter, setExcludeFilter,
    sort, handleSort,
    currentPage, setCurrentPage,
    pageSize, totalPages,
    paginatedUsers, filteredSortedUsers,
    processedUsers
  } = usePaginatedUsers(data);

  // Calculate project summary (memoized, O(n+m) with Map lookup)
  const projectSummary = useMemo<ProjectSummaryItem[]>(() => {
    if (!data) return [];
    const detailMap = new Map(data.projectDetails.map(d => [d.project.id, d]));
    return data.projects.map(project => {
      const detail = detailMap.get(project.id);
      return {
        project,
        name: project.name,
        status: project.status,
        userCount: detail?.users.length || 0,
        apiKeyCount: detail?.apiKeys.length || 0,
        createdAt: formatDate(project.created_at)
      };
    });
  }, [data]);

  if (loading) {
    return <LoadingSpinner message="Fetching organization data..." />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={() => fetchData()} />;
  }

  if (!data) {
    return <EmptyState onFetchData={() => fetchData()} />;
  }

  // Display switching based on view mode
  if (viewMode === 'project-detail') {
    if (!selectedProject || !data) return null;
    const projectDetail = data.projectDetails.find(d => d.project.id === selectedProject.id);
    if (!projectDetail) return null;
    return <ProjectDetailView selectedProject={selectedProject} projectDetail={projectDetail} onBack={handleBackToOverview} />;
  }

  if (viewMode === 'usage') {
    return <UsageDashboard timezoneMode={timezoneMode} onToggleTimezone={toggleTimezone} />;
  }

  const infoBadges = data ? [
    {
      icon: <UsersIcon className="w-2.5 h-2.5" />,
      text: `${data.users.length} users`,
      variant: 'slate' as const
    },
    {
      icon: <CheckIcon className="w-2.5 h-2.5" />,
      text: `${data.projects.filter(p => p.status === 'active').length} active`,
      variant: 'emerald' as const
    }
  ] : [];

  const headerActions = [
    <button
      key="usage"
      onClick={handleViewUsage}
      className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all flex items-center gap-1.5"
      title="View usage analytics"
    >
      <DashboardIcon className="w-3.5 h-3.5" />
      Usage Analytics
    </button>,
    <button
      key="reload"
      onClick={() => fetchData(true)}
      disabled={loading}
      className={`p-1.5 ${UI_CONSTANTS.COLORS.emerald.primary} text-white rounded transition-all disabled:opacity-50`}
      title="Reload data"
    >
      {loading ? (
        <div className={`h-3.5 w-3.5 ${UI_CONSTANTS.ANIMATIONS.spin}`}></div>
      ) : (
        <RefreshIcon className="w-3.5 h-3.5" />
      )}
    </button>
  ];

  return (
    <div className="space-y-3">
      <CompactHeader
        title="Management Dashboard"
        subtitle="Organization overview"
        icon={<ManagementIcon />}
        infoBadges={infoBadges}
        actions={headerActions}
      />

      <OrganizationSummaryCards data={data} processedUsers={processedUsers} />
      <ProjectSummaryTable projectSummary={projectSummary} onProjectClick={handleProjectClick} />
      <UserFilterBar
        filter={filter}
        setFilter={setFilter}
        excludeFilter={excludeFilter}
        setExcludeFilter={setExcludeFilter}
      />
      <UserAnalysisTable
        filteredSortedUsers={filteredSortedUsers}
        paginatedUsers={paginatedUsers}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        sort={sort}
        onSort={handleSort}
      />
      <InfoNotice />
    </div>
  );
};
